"use client";

import posthogClient from "posthog-js";
import { toast } from "sonner";
import { authErrorDescription } from "@/lib/auth-client-error";
import type { OAuthProviderConfig } from "@/lib/oauth";
import { loginHref } from "@/lib/safe-next-path";
import { signIn } from "@domainstack/auth/client";
import { Button } from "@domainstack/ui/button";
import { Spinner } from "@domainstack/ui/spinner";
import { cn } from "@domainstack/ui/utils";
interface OAuthButtonProps {
  /** Provider configuration */
  provider: OAuthProviderConfig;
  /** URL to redirect to after successful sign-in */
  callbackURL?: string;
  /** Additional class names */
  className?: string;
  /** Whether this button is loading */
  isLoading?: boolean;
  /** Whether any OAuth button is loading (disables all buttons) */
  isAnyLoading?: boolean;
  /** Callback when loading state changes */
  onLoadingChange?: (loading: boolean) => void;
}

/**
 * Generic OAuth sign-in button that works with any configured provider.
 *
 * Usage:
 * ```tsx
 * import { getEnabledProviders } from "@/lib/constants/oauth-providers";
 *
 * // Render buttons for all enabled providers
 * {getEnabledProviders().map((provider) => (
 *   <OAuthButton key={provider.id} provider={provider} />
 * ))}
 * ```
 */
export function OAuthButton({
  provider,
  callbackURL,
  className,
  isLoading = false,
  isAnyLoading = false,
  onLoadingChange
}: OAuthButtonProps) {
  const Icon = provider.icon;
  const handleSignIn = async () => {
    onLoadingChange?.(true);
    posthogClient.capture("sign_in_clicked", {
      provider: provider.id
    });

    // Reset loading state if user returns to page (e.g., via back button)
    const handleVisibilityChange = () => {
      if (document.visibilityState === "visible") {
        onLoadingChange?.(false);
        document.removeEventListener("visibilitychange", handleVisibilityChange);
      }
    };
    document.addEventListener("visibilitychange", handleVisibilityChange);
    const fail = (err: unknown, description: string) => {
      // Only reset on actual error
      document.removeEventListener("visibilitychange", handleVisibilityChange);
      onLoadingChange?.(false);
      posthogClient.captureException(err, {
        provider: provider.id,
        action: "sign_in"
      });
      toast.error(`Failed to sign in with ${provider.name}.`, {
        description
      });
    };
    try {
      const {
        error
      } = await signIn.social({
        provider: provider.id,
        callbackURL,
        // On OAuth errors, redirect to login page where errors are displayed,
        // keeping the user's destination
        errorCallbackURL: loginHref(callbackURL)
      });
      if (error) {
        // Better Auth resolves `{ error }` (e.g. a 429 from its rate limit) instead of throwing
        fail(new Error(error.message ?? `sign-in failed (${error.status})`), authErrorDescription(error));
      }
      // Don't reset loading otherwise - let it persist during navigation
      // It will be reset if user returns via back button
    } catch (err_0) {
      fail(err_0, "Please try again or choose a different provider.");
    }
  };
  return <Button size="lg" variant="outline" className={cn("w-full gap-3", className)} onClick={handleSignIn} disabled={isAnyLoading}>
      {isLoading ? <Spinner className="size-5" /> : <Icon className="size-5" />}
      Continue with {provider.name}
    </Button>;
}